export type ShippingRowLike = Record<string, unknown>;

export function shippingOptionIdsKey(rows: ShippingRowLike[]): string {
  return rows
    .map((r) => String(r.shipping__id ?? r.shipping_id ?? r.id ?? ""))
    .filter(Boolean)
    .sort()
    .join(",");
}

export function sumShippingAmount(rows: ShippingRowLike[]): number {
  return rows.reduce((s, r) => s + Number(r.shipping_amount ?? 0), 0);
}

/** Keep Order Totals manual edit when ShippingOptions recalculates rates (e.g. qty change). */
export function applyUserLockedShippingAmounts<T extends ShippingRowLike>(
  incoming: T[],
  locked: ShippingRowLike[] | null | undefined
): T[] {
  if (!locked?.length || !incoming.length) return incoming;
  return incoming.map((row, i) => {
    const lockRow =
      locked[i] ??
      locked.find(
        (l) =>
          String(l.shipping__id ?? l.shipping_id ?? "") ===
          String(row.shipping__id ?? row.shipping_id ?? "")
      ) ??
      locked[0];
    if (!lockRow) return row;
    const amt = Number(lockRow.shipping_amount ?? 0);
    return { ...row, shipping_amount: amt, cost: Number(lockRow.cost ?? amt) };
  });
}

/** Prefer live UI shipping amounts over resolve echo (tax fields still come from resolve). */
export function mergeResolvedShippingAmounts(
  resolvedRows: ShippingRowLike[],
  localRows: ShippingRowLike[] | null | undefined,
  userEditedShippingAmount: boolean
): ShippingRowLike[] {
  return resolvedRows.map((resolvedRow, i) => {
    const shipId = String(resolvedRow.shipping_id ?? resolvedRow.shipping__id ?? "");
    const localRow =
      localRows?.find(
        (r) => String(r.shipping__id ?? r.shipping_id ?? "") === shipId
      ) ?? localRows?.[i];
    if (!localRow) return resolvedRow;

    const localAmt = Number(localRow.shipping_amount ?? NaN);
    if (!Number.isFinite(localAmt)) return resolvedRow;

    if (userEditedShippingAmount || localAmt !== Number(resolvedRow.shipping_amount ?? 0)) {
      return {
        ...resolvedRow,
        shipping_amount: localAmt,
        cost: Number(localRow.cost ?? localAmt),
        shipping_tax_amount:
          localRow.shipping_tax_amount ?? resolvedRow.shipping_tax_amount,
        shipping_tax_percentage:
          localRow.shipping_tax_percentage ?? resolvedRow.shipping_tax_percentage,
      };
    }
    return resolvedRow;
  });
}

/**
 * After resolve: keep live shipping amounts from ShippingOptions / Order Totals;
 * only merge tax fields from resolve so qty/rate recalc is never overwritten.
 */
export function mergeResolvedShippingTaxIntoLocal(
  localRows: ShippingRowLike[],
  resolvedRows: ShippingRowLike[]
): ShippingRowLike[] {
  if (!localRows.length) return resolvedRows;
  return localRows.map((localRow, i) => {
    const shipId = String(localRow.shipping__id ?? localRow.shipping_id ?? "");
    const resolvedRow =
      resolvedRows.find(
        (r) => String(r.shipping_id ?? r.shipping__id ?? "") === shipId
      ) ?? resolvedRows[i];
    if (!resolvedRow) return localRow;

    const amount = Number(localRow.shipping_amount ?? 0);
    return {
      ...localRow,
      shipping_amount: amount,
      cost: Number(localRow.cost ?? amount),
      shipping_tax_amount:
        resolvedRow.shipping_tax_amount ?? localRow.shipping_tax_amount,
      shipping_tax_percentage:
        resolvedRow.shipping_tax_percentage ?? localRow.shipping_tax_percentage,
      shipping_taxable:
        resolvedRow.shipping_taxable ?? localRow.shipping_taxable,
    };
  });
}
